import { useEffect } from "react";
import { useParams, useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  Users,
  UsersRound,
  Settings,
  BarChart3,
  FileText,
  Settings2,
  CreditCard,
} from "lucide-react";
import { routes } from "@/routes";
import { useOrganisation } from "@/hooks/useOrganisation";
import { useAppStore } from "@/stores/appStore";
import { useOrganisationAuth } from "@/hooks/useOrganisationAuth";
import { ORG_ACTIONS } from "@/constants/auth";
import { useNavigation } from "@/contexts/NavigationContext";

type SectionKey =
  | "members"
  | "teams"
  | "settings"
  | "usage"
  | "billing"
  | "policies"
  | "services";

interface NavItem {
  id: SectionKey;
  label: string;
  icon: React.ReactNode;
  path: string;
}

/**
 * OrganisationLayout - Sidebar layout for organisation pages
 * Matches the SettingsPage layout with left navigation and content panel
 */
export function OrganisationLayout() {
  const { organisationId } = useParams<{ organisationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { organisation } = useOrganisation(organisationId);
  const { organisation: userOrg } = useAppStore();
  const { can } = useOrganisationAuth(userOrg?.auth);
  const isOwner = can(ORG_ACTIONS.IS_OWNER);
  const { setNavigationMeta, clearNavigationMeta } = useNavigation();

  // Set navigation meta
  useEffect(() => {
    setNavigationMeta({
      organisationId,
      organisationName: organisation?.name,
    });
    return () => clearNavigationMeta();
  }, [
    organisationId,
    organisation?.name,
    setNavigationMeta,
    clearNavigationMeta,
  ]);

  const navItems: NavItem[] = [
    {
      id: "members",
      label: "Members",
      icon: <Users className="w-4 h-4" />,
      path: organisationId ? routes.organisationMembers(organisationId) : "",
    },
    {
      id: "teams",
      label: "Teams",
      icon: <UsersRound className="w-4 h-4" />,
      path: organisationId ? routes.organisationTeams(organisationId) : "",
    },
    {
      id: "settings",
      label: "Settings",
      icon: <Settings className="w-4 h-4" />,
      path: organisationId ? routes.organisationSettings(organisationId) : "",
    },
    {
      id: "usage",
      label: "Usage",
      icon: <BarChart3 className="w-4 h-4" />,
      path: organisationId ? routes.organisationUsage(organisationId) : "",
    },
    {
      id: "services",
      label: "Services",
      icon: <Settings2 className="w-4 h-4" />,
      path: organisationId ? routes.organisationServices(organisationId) : "",
    },
    ...(isOwner
      ? [
          {
            id: "billing" as SectionKey,
            label: "Billing",
            icon: <CreditCard className="w-4 h-4" />,
            path: organisationId
              ? routes.organisationBilling(organisationId)
              : "",
          },
          {
            id: "policies" as SectionKey,
            label: "Policies",
            icon: <FileText className="w-4 h-4" />,
            path: organisationId
              ? routes.organisationPolicies(organisationId)
              : "",
          },
        ]
      : []),
  ];

  // Determine active section from current path
  const getActiveSection = (): SectionKey => {
    const path = location.pathname;
    if (path.includes("/members")) return "members";
    if (path.includes("/teams")) return "teams";
    if (path.includes("/settings")) return "settings";
    if (path.includes("/usage")) return "usage";
    if (path.includes("/billing")) return "billing";
    if (path.includes("/services")) return "services";
    if (path.includes("/policies")) return "policies";
    return "members";
  };

  const activeSection = getActiveSection();

  const handleSectionChange = (item: NavItem) => {
    if (item.path && location.pathname !== item.path) {
      navigate(item.path, { replace: true });
    }
  };

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <nav className="w-52 shrink-0 border-r border-white/10 flex flex-col gap-2 p-4 bg-[#222]">
        <p className="text-sm font-medium text-foreground mb-3 text-left">
          {organisation?.name ?? "Organisation"}
        </p>
        {navItems.map((item) => {
          const isActive = activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleSectionChange(item)}
              className="relative flex items-center gap-2.5 px-3 py-2 rounded-[8px] text-sm transition-all text-left w-full border cursor-pointer overflow-hidden"
              style={{
                backgroundColor: isActive
                  ? "rgba(227, 15, 99, 0.12)"
                  : "rgba(255, 255, 255, 0.05)",
                borderColor: isActive
                  ? "transparent"
                  : "rgba(255, 255, 255, 0.12)",
                color: isActive ? "#fff" : "#999",
                fontWeight: isActive ? 500 : 400,
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255, 255, 255, 0.1)";
                  e.currentTarget.style.borderColor =
                    "rgba(255, 255, 255, 0.2)";
                  e.currentTarget.style.color = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255, 255, 255, 0.05)";
                  e.currentTarget.style.borderColor =
                    "rgba(255, 255, 255, 0.12)";
                  e.currentTarget.style.color = "#999";
                }
              }}
            >
              {/* Pink accent bar */}
              <span
                className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[8px] transition-transform duration-200"
                style={{
                  backgroundColor: "#e30f63",
                  transform: isActive ? "scaleX(1)" : "scaleX(0)",
                  transformOrigin: "left",
                }}
              />
              {item.icon}
              {item.label}
            </button>
          );
        })}
      </nav>

      {/* Content */}
      <main className="flex-1 min-w-0 overflow-y-auto p-8">
        <div className="max-w-4xl">
          <div className="bg-[#222] border border-white/10 rounded-[8px] p-6">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}
